/**
 * Step Card illustration — miniature wireframe of a single step:
 * prompt area with rich text, config panel below (MCQ options shown).
 */
import type { IllustrationProps } from './types';
import { usePalette } from './types';

export function StepCard({ variant = 'on-gradient', className, style }: IllustrationProps) {
  const p = usePalette(variant);

  return (
    <svg
      viewBox="0 0 120 100"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      style={style}
      aria-hidden="true"
    >
      {/* Card frame */}
      <rect
        x="4"
        y="4"
        width="112"
        height="92"
        rx="10"
        fill={p.frameFill}
        stroke={p.frameStroke}
        strokeWidth="1"
      />

      {/* Header bar — type badge + step number */}
      <rect x="14" y="12" width="20" height="5" rx="2.5" fill={p.accent1} opacity="0.3" />
      <rect x="16" y="13.5" width="16" height="2" rx="1" fill={p.accent1} opacity="0.7" />
      <rect x="38" y="13" width="10" height="3" rx="1.5" fill={p.faint} />
      {/* Difficulty dots */}
      <circle cx="96" cy="14.5" r="2" fill={p.accent1} opacity="0.5" />
      <circle cx="102" cy="14.5" r="2" fill={p.accent2} opacity="0.3" />
      <circle cx="108" cy="14.5" r="2" fill={p.faint} />

      {/* Prompt area — rich text block */}
      <rect
        x="14"
        y="24"
        width="92"
        height="28"
        rx="4"
        fill={p.cardFill}
        stroke={p.cardStroke}
        strokeWidth="0.75"
      />
      {/* Text lines */}
      <rect x="20" y="30" width="74" height="3" rx="1.5" fill={p.medium} />
      <rect x="20" y="36" width="60" height="3" rx="1.5" fill={p.subtle} />
      <rect x="20" y="42" width="48" height="3" rx="1.5" fill={p.faint} />

      {/* Config area — MCQ options list */}
      <rect
        x="14"
        y="58"
        width="92"
        height="32"
        rx="4"
        fill={p.cardFill}
        stroke={p.cardStroke}
        strokeWidth="0.75"
      />

      {/* Option A — correct (highlighted) */}
      <circle
        cx="22"
        cy="66"
        r="2.5"
        fill={p.accent1}
        opacity="0.4"
        stroke={p.accent1}
        strokeWidth="0.5"
      />
      <rect x="28" y="64.5" width="40" height="3" rx="1.5" fill={p.subtle} />

      {/* Option B */}
      <circle cx="22" cy="74" r="2.5" fill="none" stroke={p.cardStroke} strokeWidth="0.75" />
      <rect x="28" y="72.5" width="34" height="3" rx="1.5" fill={p.subtle} />

      {/* Option C */}
      <circle cx="22" cy="82" r="2.5" fill="none" stroke={p.cardStroke} strokeWidth="0.75" />
      <rect x="28" y="80.5" width="46" height="3" rx="1.5" fill={p.subtle} />

      {/* Weight indicators on right */}
      <rect x="90" y="64" width="10" height="4" rx="2" fill={p.accent1} opacity="0.25" />
      <rect x="90" y="72" width="10" height="4" rx="2" fill={p.faint} />
      <rect x="90" y="80" width="10" height="4" rx="2" fill={p.faint} />
    </svg>
  );
}
